import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { useCartStore, useAuthStore } from '@/store';

// Import sub-components
import {
  RestaurantHeader,
  MenuSection,
  ReviewsSection,
  AddToCartDialog,
  TableReservationWidget,
  BranchSelector
} from '@/components/restaurant';

import { API } from '@/config/api';
import GuestChat from '@/components/chat/GuestChat';

export default function RestaurantPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const { addItem } = useCartStore();
  const { isAuthenticated, user } = useAuthStore();

  const fetchRestaurant = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurants/${id}`);
      setRestaurant(response.data);
    } catch (error) {
      toast.error('Failed to load restaurant');
      navigate('/');
    }
  });

  const fetchMenu = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurants/${id}/menu`);
      setMenu(response.data);
      if (response.data.length > 0) {
        setActiveCategory(response.data[0].name);
      }
    } catch (error) {
      console.error('Failed to fetch menu:', error);
    } finally {
      setLoading(false);
    }
  });

  const fetchReviews = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurants/${id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  });

  useEffect(() => {
    fetchRestaurant();
    fetchMenu();
    fetchReviews();
  }, [id, fetchRestaurant, fetchMenu, fetchReviews]);

  const fetchModifiers = async (itemId) => {
    try {
      const response = await axios.get(`${API}/menu-items/${itemId}/modifiers`);
      setModifiers(response.data);
    } catch (error) {
      console.error('Failed to fetch modifiers:', error);
    }
  };

  const handleItemClick = async (item) => {
    setSelectedItem(item);
    setQuantity(1);
    setSpecialInstructions('');
    setSelectedModifiers([]);
    await fetchModifiers(item.id);
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/auth?redirect=' + encodeURIComponent(`/restaurant/${id}`));
      return;
    }

    const result = await addItem(
      selectedItem.id,
      quantity,
      specialInstructions || null,
      selectedModifiers.length > 0 ? selectedModifiers : null
    );

    if (result.success) {
      toast.success(`${selectedItem.name} added to cart`);
      setSelectedItem(null);
    } else {
      toast.error(result.error || 'Failed to add item');
    }
  };

  const toggleModifier = (modifierId) => {
    setSelectedModifiers(prev => 
      prev.includes(modifierId) 
        ? prev.filter(id => id !== modifierId)
        : [...prev, modifierId]
    );
  };

  const calculateItemTotal = () => {
    let total = selectedItem?.price || 0;
    if (selectedModifiers.length > 0) {
      modifiers.forEach(group => {
        group.options.forEach(opt => {
          if (selectedModifiers.includes(opt.id)) {
            total += opt.price;
          }
        });
      });
    }
    return total * quantity;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Skeleton className="h-64 w-full" />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-1/3 mb-4" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="restaurant-page">
      <RestaurantHeader 
        restaurant={restaurant} 
        onBack={() => navigate(-1)} 
      />

      {/* Branch Selector for Multi-location Restaurants */}
      {restaurant?.is_multi_location && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <BranchSelector
            restaurantId={parseInt(id)}
            selectedBranch={selectedBranch}
            onSelectBranch={setSelectedBranch}
          />
        </div>
      )}

      {/* Menu Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="menu">
          <div className="flex items-center justify-between mb-6">
            <TabsList className="h-auto p-1 bg-muted/50 rounded-xl">
              <TabsTrigger value="menu" className="rounded-lg">Menu</TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-lg">Reviews ({reviews.length})</TabsTrigger>
            </TabsList>
            
            {/* Table Reservation Button */}
            <TableReservationWidget 
              restaurantId={parseInt(id)} 
              restaurantName={restaurant?.name} 
            />
          </div>

          <TabsContent value="menu">
            <MenuSection 
              menu={menu}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              onItemClick={handleItemClick}
            />
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsSection reviews={reviews} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add to Cart Dialog */}
      <AddToCartDialog
        selectedItem={selectedItem}
        onClose={() => setSelectedItem(null)}
        modifiers={modifiers}
        selectedModifiers={selectedModifiers}
        onToggleModifier={toggleModifier}
        specialInstructions={specialInstructions}
        setSpecialInstructions={setSpecialInstructions}
        quantity={quantity}
        setQuantity={setQuantity}
        calculateItemTotal={calculateItemTotal}
        onAddToCart={handleAddToCart}
      />

      {restaurant && <GuestChat restaurantId={restaurant.id || parseInt(id)} restaurantName={restaurant.name} sourcePage="restaurant" />}
    </div>
  );
}
